/** Quick 260713-kk0 — NotificationBellComponent (NTF-07, NTF-03, NTF-10).
 *
 * Toolbar bell + unread badge + dropdown panel for the in-app notification inbox. Standalone;
 * dropped into app.component.html between the toolbar spacer and the username block for authed users.
 *
 * Behaviour:
 *   - On init: loads the caller's inbox (list + unreadCount) via NotificationsApiService.
 *   - Badge: matBadge = unreadCount, hidden when 0.
 *   - Panel: lists items newest-first with event label (reused EVENT_LABELS), message, timestamp;
 *     unread rows are visually distinct.
 *   - Click a row: markRead(id) → decrement badge → router.navigate(item.linkUrl) (fallback '/').
 *   - "Mark all read": markAllRead() → zero the badge + flag rows read locally.
 *   - Live push: subscribes to the EXISTING NotificationHubService.push$; each push increments the
 *     badge and prepends a synthesized unread row (no second hub connection; authoritative list
 *     refreshes on next open).
 */

import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { Subscription } from 'rxjs';

import { NotificationsApiService, InboxItem } from '../services/notifications-api.service';
import { NotificationHubService } from '../services/notification-hub.service';
import type { NotificationPush } from '../models/notification-push';
import { EVENT_LABELS } from '../../features/notification-prefs/components/pref-row.component';
import { UtcDatePipe } from '../../shared/pipes/utc-date.pipe';

@Component({
  selector: 'nepf-notification-bell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatBadgeModule,
    MatMenuModule,
    MatDividerModule,
    UtcDatePipe,
  ],
  template: `
    <button
      mat-icon-button
      [matMenuTriggerFor]="menu"
      (menuOpened)="onOpen()"
      aria-label="Notifications"
      [matBadge]="unreadCount()"
      [matBadgeHidden]="unreadCount() === 0"
      matBadgeColor="warn"
      matBadgeSize="small"
    >
      <mat-icon>notifications</mat-icon>
    </button>

    <mat-menu #menu="matMenu" class="nepf-notif-panel" xPosition="before">
      <div class="nepf-notif-header" (click)="$event.stopPropagation()">
        <span class="nepf-notif-title">Notifications</span>
        <!-- quick-260729 (QA 21374 Bug 6): a disabled "Mark all read" is CORRECT when nothing is
             unread, but indistinguishable from broken — the wrapper carries the explanation
             (title works on the wrapper even while the button itself is disabled). -->
        <span [title]="unreadCount() === 0 ? 'No unread notifications' : ''">
          <button
            mat-button
            type="button"
            [disabled]="unreadCount() === 0"
            (click)="markAllRead(); $event.stopPropagation()"
          >
            Mark all read
          </button>
        </span>
      </div>
      <mat-divider></mat-divider>

      @if (loadError()) {
        <div class="nepf-notif-empty" role="alert">
          Couldn't load notifications. They'll retry when you reopen this panel.
        </div>
      } @else if (items().length === 0) {
        <div class="nepf-notif-empty">You're all caught up.</div>
      } @else {
        @for (item of items(); track item.id) {
          <button
            mat-menu-item
            class="nepf-notif-row"
            [class.nepf-notif-unread]="!item.readAtUtc"
            (click)="open(item)"
          >
            <!-- quick-260729 (QA 21374 Bug 5): Material 21 wraps ng-content in a single
                 .mat-mdc-menu-item-text span, so the flex-column must live on an inner div —
                 without it the three lines render as one run-on line. The dot is the explicit
                 unread cue (the old tint alone was invisible under the hover state layer). -->
            <div class="nepf-notif-row__stack">
              <span class="nepf-notif-event">
                @if (!item.readAtUtc) {
                  <span class="nepf-notif-dot" aria-hidden="true"></span>
                  <span class="cdk-visually-hidden">Unread:</span>
                }
                {{ labelFor(item.eventType) }}
              </span>
              <span class="nepf-notif-message">{{ item.message }}</span>
              <!-- quick-260807-s2w (DEMO-03): the API serializes sentAtUtc WITHOUT a zone
                   designator and the plain date pipe reads a zone-less string as LOCAL, so
                   every bell timestamp was shifted by the viewer's UTC offset. utcDate fixes
                   the INPUT interpretation; output stays in the viewer's local zone. -->
              <span class="nepf-notif-time">{{ item.sentAtUtc | utcDate: 'short' }}</span>
            </div>
          </button>
        }
      }

      <mat-divider></mat-divider>
      <button mat-menu-item (click)="viewAll()">
        <mat-icon>settings</mat-icon>
        <span>Notification settings</span>
      </button>
    </mat-menu>
  `,
  styles: [
    `
      .nepf-notif-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 4px 8px 4px 16px;
        min-width: 320px;
      }
      .nepf-notif-title {
        font-weight: 600;
      }
      .nepf-notif-empty {
        padding: 16px;
        opacity: 0.7;
      }
      .nepf-notif-row {
        height: auto;
        line-height: 1.3;
        padding-top: 8px;
        padding-bottom: 8px;
        white-space: normal;
      }
      /* Material 21's menu item wraps content in .mat-mdc-menu-item-text — the stacking
         column lives on our own inner div so the wrapper can't flatten it. */
      .nepf-notif-row__stack {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 2px;
      }
      .nepf-notif-event {
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        opacity: 0.7;
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .nepf-notif-message {
        font-weight: 500;
      }
      .nepf-notif-time {
        font-size: 0.75rem;
        opacity: 0.6;
      }
      /* Explicit unread cue — a dot survives Material's hover state layer, unlike the tint. */
      .nepf-notif-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--mat-sys-primary, #1976d2);
        flex: none;
      }
      /* Tripled selector ties .mat-mdc-menu-item:not([disabled]):hover at (0,3,0) and wins on
         order, and the explicit :hover keeps the tint (slightly stronger) while pointing — the
         old single-class tint vanished exactly when the user hovered the row. */
      .nepf-notif-unread.nepf-notif-unread.nepf-notif-unread {
        background: rgba(25, 118, 210, 0.08);
      }
      .nepf-notif-unread.nepf-notif-unread.nepf-notif-unread:hover {
        background: rgba(25, 118, 210, 0.14);
      }
      .nepf-notif-unread .nepf-notif-message {
        font-weight: 700;
      }
    `,
  ],
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  private readonly api = inject(NotificationsApiService);
  private readonly hub = inject(NotificationHubService);
  private readonly router = inject(Router);

  readonly items = signal<InboxItem[]>([]);
  readonly unreadCount = signal<number>(0);
  /** quick-260729 (QA 21374 Bug 5): surfaced instead of silently swallowed — an expired-token
   *  401 used to be indistinguishable from an empty inbox. */
  readonly loadError = signal(false);

  /** Kept for potential template use / debugging: whether there is anything to show. */
  readonly hasItems = computed(() => this.items().length > 0);

  private pushSub?: Subscription;

  ngOnInit(): void {
    this.refresh();
    // Live push: reuse the EXISTING hub connection — do NOT create a second one.
    this.pushSub = this.hub.push$.subscribe((push) => this.onPush(push));
  }

  ngOnDestroy(): void {
    this.pushSub?.unsubscribe();
  }

  /** (Re)load the authoritative list + unread count. */
  refresh(): void {
    this.api.list(undefined, 50, 0).subscribe({
      next: (res) => {
        this.items.set(res.items);
        this.unreadCount.set(res.unreadCount);
        this.loadError.set(false);
      },
      error: () => {
        // Keep whatever list we have, but say the refresh failed — a silent swallow made an
        // expired session read as "no notifications" (QA 21374 Bug 5).
        this.loadError.set(this.items().length === 0);
      },
    });
  }

  /** Refresh the authoritative list each time the panel opens. */
  onOpen(): void {
    this.refresh();
  }

  labelFor(eventType: string): string {
    return EVENT_LABELS[eventType] ?? eventType;
  }

  /** Mark one read (optimistic), then deep-link. */
  open(item: InboxItem): void {
    if (!item.readAtUtc) {
      this.api.markRead(item.id).subscribe({ next: () => undefined, error: () => undefined });
      this.items.update((rows) =>
        rows.map((r) => (r.id === item.id ? { ...r, readAtUtc: new Date().toISOString() } : r)),
      );
      this.unreadCount.update((c) => Math.max(0, c - 1));
    }
    void this.router.navigate([item.linkUrl || '/']);
  }

  /** Clear the badge + flag every row read locally. */
  markAllRead(): void {
    this.api.markAllRead().subscribe({ next: () => undefined, error: () => undefined });
    const now = new Date().toISOString();
    this.items.update((rows) => rows.map((r) => (r.readAtUtc ? r : { ...r, readAtUtc: now })));
    this.unreadCount.set(0);
  }

  /** No dedicated history list route yet — link to the notification settings page. */
  viewAll(): void {
    void this.router.navigate(['/settings/notifications']);
  }

  /**
   * Live push: increment the badge and prepend a synthesized unread row so the user sees it
   * immediately. correlationId is not the persisted notification id, so this row's id is derived
   * from correlationId for template tracking; the authoritative list (with the real id) replaces it
   * on next open/refresh.
   */
  private onPush(push: NotificationPush): void {
    if (!push || typeof push.correlationId !== 'string' || push.correlationId.length === 0) {
      return;
    }
    const synthesized: InboxItem = {
      id: push.correlationId,
      eventType: push.eventType,
      subject: push.subject,
      message: push.message,
      linkUrl: push.linkUrl || '/',
      sentAtUtc: push.createdAt ?? new Date().toISOString(),
      readAtUtc: null,
    };
    this.items.update((rows) => [synthesized, ...rows]);
    this.unreadCount.update((c) => c + 1);
  }
}
